import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type RenderFormat = 'pdf' | 'xlsx' | 'csv' | 'html' | 'json';

/**
 * CSAGE-119/124: render mode controls how the body fragment is wrapped.
 *   live  — continuous-scroll HTML, no print chrome, interactivity layer
 *           (default for in-app browsing)
 *   page  — paginated chrome (header/footer/page numbers + @page bounds)
 *           identical to PDF output, no interactivity (default for
 *           scheduled jobs / regulator audience)
 *   embed — minimal chrome, sized to host container (3rd-party iframe)
 */
export type RenderMode = 'live' | 'page' | 'embed';

export interface RenderResult {
  runId: string;
  format: RenderFormat;
  outputUri: string;
  byteLength: number;
  rowCount: number;
  durationMs: number;
  contentType: string;
}

export interface ReportRunSummary {
  id: string;
  tenantId: string;
  templateId: string | null;
  reportId: string | null;
  versionUsed: number | null;
  params: Record<string, unknown>;
  startedAt: string;
  durationMs: number | null;
  rowCount: number | null;
  status: string;
  outputUri: string | null;
  outputContentType: string | null;
  format: 'pdf' | 'xlsx' | 'csv' | 'html' | 'json' | 'other' | null;
}

const EXTENSION: Record<RenderFormat, string> = {
  pdf: 'pdf',
  xlsx: 'xlsx',
  csv: 'csv',
  html: 'html',
  json: 'json',
};

/**
 * Renders a template and resolves to a `RenderedOutput` with an
 * in-memory blob + object URL ready for the inline viewer.
 * The caller is responsible for revoking the URL when the viewer
 * unmounts (useEffect cleanup).
 */
export interface RenderedOutput {
  runId: string;
  format: RenderFormat;
  contentType: string;
  byteLength: number;
  rowCount: number;
  durationMs: number;
  blobUrl: string;
  /** For text-shaped outputs (csv, json, html) we parse the body too so
   *  tabular / JSON-tree viewers can render without a second fetch. */
  text: string | null;
  /** Resolved render mode echoed by the server in x-sage-mode. The
   *  panel uses this (rather than the toggle's current value) to pick
   *  the outer chrome — what's actually displayed beats what the user
   *  has staged for next Run. */
  mode?: RenderMode;
  /** Resolved paper geometry echoed by the server. The paged viewer
   *  uses these to size the viewport so it matches the rendered
   *  HTML's chrome (template settings + per-instance overrides). */
  paperOrientation?: 'portrait' | 'landscape';
  paperSize?: 'letter' | 'legal' | 'a4' | 'tabloid';
}

export function useRenderInline(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    RenderedOutput,
    Error,
    { format: RenderFormat; params?: Record<string, unknown>; mode?: RenderMode }
  >({
    mutationFn: async ({ format, params = {}, mode }): Promise<RenderedOutput> => {
      const res = await api.fetchRaw(`/report-templates/${templateId}/render`, {
        method: 'POST',
        body: JSON.stringify(mode ? { format, params, mode } : { format, params }),
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        const err = new Error(`Render failed (${res.status})`) as Error & { body?: unknown };
        err.body = body;
        throw err;
      }
      const runId = res.headers.get('x-canopy-run-id') ?? '';
      const rowCount = Number(res.headers.get('x-canopy-row-count') ?? '0');
      const durationMs = Number(res.headers.get('x-canopy-duration-ms') ?? '0');
      const contentType = res.headers.get('content-type') ?? 'application/octet-stream';
      const resolvedMode = res.headers.get('x-sage-mode');
      const paperOrientation = res.headers.get('x-sage-paper-orientation');
      const paperSize = res.headers.get('x-sage-paper-size');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      // For text-shaped outputs we also surface the body as a string
      // so CSV / JSON / HTML viewers can work without a refetch.
      let text: string | null = null;
      if (format === 'csv' || format === 'json' || format === 'html') {
        try {
          text = await blob.text();
        } catch {
          text = null;
        }
      }

      return {
        runId,
        format,
        contentType,
        byteLength: blob.size,
        rowCount,
        durationMs,
        blobUrl,
        text,
        ...(resolvedMode === 'live' || resolvedMode === 'page' || resolvedMode === 'embed'
          ? { mode: resolvedMode as RenderMode }
          : {}),
        ...(paperOrientation === 'portrait' || paperOrientation === 'landscape'
          ? { paperOrientation: paperOrientation as 'portrait' | 'landscape' }
          : {}),
        ...(paperSize === 'letter' ||
        paperSize === 'legal' ||
        paperSize === 'a4' ||
        paperSize === 'tabloid'
          ? { paperSize: paperSize as 'letter' | 'legal' | 'a4' | 'tabloid' }
          : {}),
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['template-runs'] });
    },
  });
}

/**
 * Lists recent runs for a template. Polls every 5s while a run is
 * in flight; idle otherwise.
 */
export function useTemplateRuns(templateId: string | null, limit = 25) {
  const api = useApi();
  return useQuery<ReportRunSummary[]>({
    queryKey: ['template-runs', templateId, limit],
    queryFn: () => api.fetch(`/report-templates/${templateId}/runs?limit=${limit}`),
    enabled: !!templateId,
    refetchInterval: 15_000,
  });
}

/**
 * Fetches an existing run's output as a fresh in-memory blob so the
 * viewer can re-display it without triggering a download.
 */
export async function fetchRunOutput(
  apiFetchRaw: (path: string, init?: RequestInit) => Promise<Response>,
  runId: string,
  format: RenderFormat,
): Promise<RenderedOutput> {
  const res = await apiFetchRaw(`/runs/${runId}/download?inline=1`, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`Fetch run failed (${res.status})`);
  }
  const contentType = res.headers.get('content-type') ?? 'application/octet-stream';
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  let text: string | null = null;
  if (format === 'csv' || format === 'json' || format === 'html') {
    try {
      text = await blob.text();
    } catch {
      text = null;
    }
  }
  return {
    runId,
    format,
    contentType,
    byteLength: blob.size,
    rowCount: 0,
    durationMs: 0,
    blobUrl,
    text,
  };
}

/**
 * Render a template and trigger a browser download of the artifact.
 * We go through the binary `/render` endpoint, then hand the Blob to
 * the browser via an object URL.
 */
export function useRenderAndDownload(templateId: string | null) {
  const api = useApi();
  return useMutation<
    RenderResult,
    Error,
    {
      format: RenderFormat;
      templateName: string;
      params?: Record<string, unknown>;
      mode?: RenderMode;
    }
  >({
    mutationFn: async ({ format, templateName, params = {}, mode }): Promise<RenderResult> => {
      const res = await api.fetchRaw(`/report-templates/${templateId}/render`, {
        method: 'POST',
        body: JSON.stringify(mode ? { format, params, mode } : { format, params }),
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        const err = new Error(`Render failed (${res.status})`) as Error & { body?: unknown };
        err.body = body;
        throw err;
      }

      const runId = res.headers.get('x-canopy-run-id') ?? '';
      const rowCount = Number(res.headers.get('x-canopy-row-count') ?? '0');
      const durationMs = Number(res.headers.get('x-canopy-duration-ms') ?? '0');
      const contentType = res.headers.get('content-type') ?? 'application/octet-stream';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safeFileName(templateName, format);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return {
        runId,
        format,
        outputUri: '',
        byteLength: blob.size,
        rowCount,
        durationMs,
        contentType,
      };
    },
  });
}

function safeFileName(name: string, fmt: RenderFormat): string {
  const base =
    name
      .replace(/[^a-zA-Z0-9 _-]+/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'report';
  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
  return `${base}-${stamp}.${EXTENSION[fmt]}`;
}
